import { useEffect, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"

import useT from "utils/hooks/useT"
import OosInvestigationModal from "components/Lims/OosInvestigationModal"
import ESignatureModal from "components/Lims/ESignatureModal"
import { FiAlertTriangle } from "react-icons/fi"
import { limsOos, limsSampleQc, limsReruns, message } from "state_management"
import { canSign, isGmpRelaxed } from "state_management/lims/segregationOfDuties"

// Investigations (SPEC-022 K10) — the TEAM's entry to in-flight OOS investigations. Phase 1 is lab
// work, so the open-investigations worklist lives on its own LIMS menu item (it was previously a
// second section on QA Analysis Review). Lists every OPEN OOS investigation (sample / analysis /
// phase / state) with an Open action that drives the OosInvestigationModal. QA Analysis Review now
// shows only the subset QA must decide (state "qa-review").
export default function Investigations() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const investigations = useSelector(limsOos.selectOpenInvestigations, shallowEqual)
  // K13 (c): closed investigations are the candidates for a signed REOPEN; shown only when toggled.
  const closedInvestigations = useSelector(limsOos.selectClosedInvestigations, shallowEqual)
  // The current member's QA gate (capability only; the per-record four-eyes ≠ closedBy is folded in
  // per row). Ghosts the "Reopen investigation" affordance (GMP: everyone SEES it, only QA acts).
  const qaCapabilityGate = useSelector((s) => canSign(s, { capability: "qa" }), shallowEqual)
  const currentMemberId = useSelector((s) => s.auth.memberId)
  const gmpRelaxed = useSelector(isGmpRelaxed) // SPEC-025: dev-only relaxation of the four-eyes guard

  const [activeOosId, setActiveOosId] = useState(null) // the open investigation being viewed, or null
  const [showClosed, setShowClosed] = useState(false) // K13 (c): reveal the closed-investigations table
  const [reopening, setReopening] = useState(null) // the closed OOS record pending a signed reopen, or null
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    dispatch(limsOos.fetch())
    // The modal reads the sample's batch/lot (batch-hold disposition) and whether a rerun already
    // exists for the sample — load both so the close flow has the data without a per-open fetch.
    dispatch(limsSampleQc.fetch())
    dispatch(limsReruns.fetch())
  }, [dispatch])

  // K13 (c): may the current user reopen THIS closed investigation? Needs the QA capability AND
  // must not be the person who closed it (four-eyes ≠ closedBy). Returns the ghost reason key, or null.
  const reopenGhostKey = (oos) => {
    if (!qaCapabilityGate.allowed) return "limsReopenGhostCapability"
    // SPEC-025: skip the four-eyes (≠ closedBy) guard when controls are relaxed (dev only).
    if (!gmpRelaxed && oos.closedBy && oos.closedBy === currentMemberId) return "limsReopenGhostFourEyes"
    return null
  }

  // K13 (c): reopen the closed investigation under a QA signature with a MANDATORY reason. The thunk
  // moves it back to `investigating`, appends reopenHistory and re-flags the sample (FE mirror, via
  // samples.flagSample). Refetch the OOS register so the row moves into the open list and the
  // re-blocked release gate re-evaluates. Returns the promise so the dialog shows 403/409/422 inline.
  const handleReopen = (signature) => {
    const oos = reopening
    setSaving(true)
    return dispatch(limsOos.reopen(oos.id, signature))
      .then(() => {
        dispatch(limsOos.fetch())
        setReopening(null)
        dispatch(message.success(t("limsReopenDone")))
      })
      .finally(() => setSaving(false))
  }

  return (
    <div className="LimsQueue">
      <h1 className="LimsQueue-title">{t("limsInvestigations")}</h1>
      <p className="LimsQueue-intro">{t("limsInvestigationsIntro")}</p>

      {investigations.length === 0 ? (
        <p className="LimsQueue-empty">{t("limsOosOpenInvestigationsEmpty")}</p>
      ) : (
        <Table striped hover responsive className="LimsQueue-table LimsQueue-table--narrow">
          <thead>
            <tr>
              <th>{t("limsInvestigationType")}</th>
              <th>{t("limsOosOpenInvestigationsSample")}</th>
              <th>{t("limsOosOpenInvestigationsAnalysis")}</th>
              <th>{t("limsOosOpenInvestigationsPhase")}</th>
              <th>{t("limsOosState")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {investigations.map((oos) => (
              <tr key={oos.id}>
                <td>
                  <InvestigationTypeBadge type={oos.type} t={t} />
                </td>
                <td>{oos.sampleLabel || "—"}</td>
                <td>{oos.analysisLabel || "—"}</td>
                <td>{t(oos.phase2 ? "limsOosPhase2Label" : "limsOosPhase1Label")}</td>
                <td style={{ whiteSpace: "nowrap" }}>{t(investigationStateKey(oos.state))}</td>
                <td className="LimsQueue-actions">
                  <div className="LimsQueue-actions-inner">
                    {/* RR2 #17: flag investigations awaiting a QA decision so QA sees what needs action. */}
                    {oos.state === "qa-review" && (
                      <FiAlertTriangle
                        title={t("limsOosAwaitingQa")}
                        style={{ color: "#fd7e14", flexShrink: 0 }}
                      />
                    )}
                    <Button variant="primary" size="sm" onClick={() => setActiveOosId(oos.id)}>
                      {t("limsQaReviewOpen")}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* K13 (c): the closed investigations — hidden behind a toggle (the screen is primarily the
          team's open-work list). QA can REOPEN a wrongly-closed one as a signed correction. */}
      <Form.Check
        type="checkbox"
        id="investigations-show-closed"
        className="LimsQueue-toggle"
        checked={showClosed}
        onChange={(e) => setShowClosed(e.target.checked)}
        label={t("limsInvestigationsShowClosed", { count: closedInvestigations.length })}
      />
      {showClosed &&
        (closedInvestigations.length === 0 ? (
          <p className="LimsQueue-empty">{t("limsInvestigationsClosedEmpty")}</p>
        ) : (
          <Table striped hover responsive className="LimsQueue-table LimsQueue-table--narrow">
            <thead>
              <tr>
                <th>{t("limsInvestigationType")}</th>
                <th>{t("limsOosOpenInvestigationsSample")}</th>
                <th>{t("limsOosOpenInvestigationsAnalysis")}</th>
                <th>{t("limsOosOpenInvestigationsPhase")}</th>
                <th>{t("limsOosState")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {closedInvestigations.map((oos) => {
                const ghostKey = reopenGhostKey(oos)
                return (
                  <tr key={oos.id}>
                    <td>
                      <InvestigationTypeBadge type={oos.type} t={t} />
                    </td>
                    <td>{oos.sampleLabel || "—"}</td>
                    <td>{oos.analysisLabel || "—"}</td>
                    <td>{t(oos.phase2 ? "limsOosPhase2Label" : "limsOosPhase1Label")}</td>
                    <td>{t(investigationStateKey(oos.state))}</td>
                    <td className="LimsQueue-actions">
                      <div className="LimsQueue-actions-inner">
                        <Button variant="outline-secondary" size="sm" onClick={() => setActiveOosId(oos.id)}>
                          {t("limsQaReviewOpen")}
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          disabled={saving || !!ghostKey}
                          title={ghostKey ? t(ghostKey) : undefined}
                          onClick={() => setReopening(oos)}
                        >
                          {t("limsReopenAction")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        ))}

      {activeOosId && (
        <OosInvestigationModal
          oosId={activeOosId}
          onClose={() => setActiveOosId(null)}
          onClosed={() => setActiveOosId(null)}
        />
      )}

      {/* K13 (c): the QA reopen signature — mandatory reason, four-eyes excludes the closer. */}
      {reopening && (
        <ESignatureModal
          title={t("limsReopenAction")}
          requiredCapability="qa"
          requireReason
          excludedMemberIds={reopening.closedBy ? [reopening.closedBy] : []}
          onSign={handleReopen}
          onCancel={() => setReopening(null)}
        />
      )}
    </div>
  )
}

// i18n key for an OOS lifecycle state shown in the investigations worklist.
function investigationStateKey(state) {
  switch (state) {
    case "investigating":
      return "limsOosStateInvestigating"
    case "qa-review":
      return "limsOosStateQaReview"
    case "closed":
      return "limsOosStateClosed"
    default:
      return "limsOosStateOpen"
  }
}

// SPEC-037 (F5): the OOS/OOT type badge in the investigations worklist so QA sees at a glance which
// kind each row is (an OOT is the same Phase-1 lab investigation, barred from Phase 2). A missing
// type reads as OOS (the historical default before OOT investigations existed).
function InvestigationTypeBadge({ type, t }) {
  const isOot = type === "OOT"
  return (
    <span className={`Investigations-type is-${isOot ? "oot" : "oos"}`}>
      {t(isOot ? "limsInvestigationTypeOot" : "limsInvestigationTypeOos")}
    </span>
  )
}
